import { useState } from 'react';
import { IRRInputs } from '../../types/irr';
import { calculateIRR, formatPercentage } from '../../lib/irrCalculator';
import { calculateLoanVsLeaseSaving } from '../../lib/loanVsLeaseSavingCalculator';
import { calculateTreasuryVsLeaseSaving } from '../../lib/treasuryVsLeaseSavingCalculator';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  TrendingUp, TrendingDown, Download, Sparkles, Banknote, Landmark, ArrowLeft,
} from 'lucide-react';
import { toast } from 'react-toastify';

type PartialIRRInputs = Omit<IRRInputs, 'corporateTaxBenefit' | 'roiOnLoan' | 'depreciationPercent'>;

interface IRRCheckDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  partialInputs: PartialIRRInputs;
}

interface AdditionalFields {
  corporateTaxBenefit: number;
  roiOnLoan: number;
  depreciationPercent: number;
}

function formatINR(value: number): string {
  const absVal = Math.abs(value);
  if (absVal >= 10000000) return `₹${(value / 10000000).toFixed(2)} Cr`;
  if (absVal >= 100000) return `₹${(value / 100000).toFixed(2)} L`;
  return `₹${value.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
}

export default function IRRCheckDialog({ open, onOpenChange, partialInputs }: IRRCheckDialogProps) {
  const [step, setStep] = useState<'form' | 'results'>('form');
  const [fields, setFields] = useState<AdditionalFields>({
    corporateTaxBenefit: 33.60,
    roiOnLoan: 7.5,
    depreciationPercent: 15,
  });

  const [irrResult, setIrrResult] = useState<ReturnType<typeof calculateIRR> | null>(null);
  const [loanSaving, setLoanSaving] = useState<{ savingAmount: number; savingPercent: number } | null>(null);
  const [treasurySaving, setTreasurySaving] = useState<{ savingAmount: number; savingPercent: number } | null>(null);

  const buildFullInputs = (): IRRInputs => ({ ...partialInputs, ...fields });

  const handleCalculate = () => {
    try {
      const fullInputs = buildFullInputs();
      const result = calculateIRR(fullInputs);
      setIrrResult(result);

      const loan = calculateLoanVsLeaseSaving(fullInputs);
      setLoanSaving({ savingAmount: loan.savingOnLoan, savingPercent: loan.savingOnLoanPercent });

      const treasury = calculateTreasuryVsLeaseSaving(fullInputs);
      setTreasurySaving({ savingAmount: treasury.savingOnTreasury, savingPercent: treasury.savingOnTreasuryPercent });

      setStep('results');
    } catch (e: any) {
      toast.error(e.message || 'Calculation error');
    }
  };

  const handleExport = (type: 'irr' | 'loan' | 'treasury') => {
    toast.info('Excel export requires backend setup. Connect Lovable Cloud to enable exports.');
  };

  const handleOpenChange = (v: boolean) => {
    if (!v) setStep('form');
    onOpenChange(v);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-lg">
            {step === 'form' ? 'Additional Parameters' : 'IRR & Saving Analysis'}
          </DialogTitle>
          <DialogDescription>
            {step === 'form'
              ? 'Fill in the missing fields to calculate IRR and savings.'
              : 'Results based on your inputs.'}
          </DialogDescription>
        </DialogHeader>

        {step === 'form' ? (
          <div className="space-y-5 pt-2">
            <div className="space-y-1.5">
              <Label>Corporate Tax Benefit (%)</Label>
              <Input
                type="number"
                step="0.01"
                value={fields.corporateTaxBenefit}
                onChange={(e) => setFields((f) => ({ ...f, corporateTaxBenefit: Number(e.target.value) }))}
              />
            </div>

            <div className="space-y-1.5">
              <Label>RoI on Loan / Treasury (%)</Label>
              <Input
                type="number"
                step="0.1"
                value={fields.roiOnLoan}
                onChange={(e) => setFields((f) => ({ ...f, roiOnLoan: Number(e.target.value) }))}
              />
            </div>

            <div className="space-y-1.5">
              <Label>Depreciation (%)</Label>
              <Input
                type="number"
                step="0.1"
                value={fields.depreciationPercent}
                onChange={(e) => setFields((f) => ({ ...f, depreciationPercent: Number(e.target.value) }))}
              />
            </div>

            <Button onClick={handleCalculate} className="w-full bg-blue-600 hover:bg-blue-700 text-white">
              <Sparkles className="mr-2 h-4 w-4" /> Calculate IRR
            </Button>
          </div>
        ) : (
          <div className="space-y-4 pt-2">
            {irrResult && (
              <div className="flex gap-3">
                <IRRKpi label="IRR Rental (Excl GST)" description="Before tax" value={irrResult.irrRentalExclGst} />
                <IRRKpi label="IRR Actual Cost" description="After tax" value={irrResult.irrActualCost} />
              </div>
            )}

            <div className="flex gap-3">
              {loanSaving && (
                <SavingKpi label="Saving on Loan" icon={Banknote} amount={loanSaving.savingAmount} percent={loanSaving.savingPercent} />
              )}
              {treasurySaving && (
                <SavingKpi label="Saving on Treasury" icon={Landmark} amount={treasurySaving.savingAmount} percent={treasurySaving.savingPercent} />
              )}
            </div>

            {/* <div className="flex gap-2 flex-wrap pt-2">
              <Button onClick={() => handleExport('irr')} className="flex-1 bg-[#e7e4d9] hover:bg-[#dbd7c9] text-gray-800">
                <Download className="mr-2 h-4 w-4" />Export IRR
              </Button>
              <Button onClick={() => handleExport('loan')} variant="outline" className="flex-1">
                <Download className="mr-2 h-4 w-4" />Loan vs Lease
              </Button>
              <Button onClick={() => handleExport('treasury')} variant="outline" className="flex-1">
                <Download className="mr-2 h-4 w-4" />Treasury vs Lease
              </Button>
            </div> */}

            <Button variant="ghost" onClick={() => setStep('form')} className="w-full">
              <ArrowLeft className="mr-2 h-4 w-4" /> Back to Inputs
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ---- Inline sub-components ---- */

function IRRKpi({ label, description, value }: { label: string; description: string; value: number }) {
  const positive = value >= 0;
  return (
    <div className={`flex-1 p-4 rounded-xl border transition-all ${
      positive
        ? 'bg-gradient-to-br from-green-50 to-emerald-50 border-green-200'
        : 'bg-gradient-to-br from-red-50 to-red-100 border-red-200'
    }`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-medium text-gray-600">{label}</p>
          <p className="text-[10px] text-gray-400">{description}</p>
        </div>
        {positive ? <TrendingUp className="h-4 w-4 text-green-500" /> : <TrendingDown className="h-4 w-4 text-red-500" />}
      </div>
      <p className="mt-2 text-2xl font-bold font-mono tracking-tight">{formatPercentage(value)}</p>
    </div>
  );
}

function SavingKpi({ label, icon: Icon, amount, percent }: { label: string; icon: React.ElementType; amount: number; percent: number }) {
  const positive = amount > 0;
  return (
    <div className={`flex-1 p-4 rounded-xl border transition-all ${
      positive
        ? 'bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200'
        : 'bg-gradient-to-br from-red-50 to-red-100 border-red-200'
    }`}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className="h-4 w-4 text-blue-600" />
        <p className="text-xs font-medium text-gray-600">{label}</p>
      </div>
      <p className="text-2xl font-bold font-mono tracking-tight">{(percent * 100).toFixed(1)}%</p>
      <p className="text-xs font-mono text-gray-500 mt-0.5">{formatINR(amount)}</p>
    </div>
  );
}
